import { categorizeAsset } from '@odo/shared-ui';
import api from '../api/config';
import { createImageThumbnail } from '../utils/imageThumbnail';

export type PatientFileType =
    'CBCT' | 'ADDITIONAL' | 'RADIOGRAPH' | 'STL' | 'PHOTO' | 'PROFILE_PICTURE' | 'CHAT_ATTACHMENT';

export interface PatientFile {
    id: string;
    patientId: string;
    fileId: string;
    fileType: PatientFileType;
    description: string | null;
    metadata: Record<string, any> | null;
    createdAt: string;
    updatedAt: string;
    file: {
        id: string;
        bucket: string;
        objectPath: string;
        mimeType: string;
        sizeBytes: string;
        originalFilename: string;
        status: 'CREATED' | 'UPLOADING' | 'READY' | 'FAILED' | 'QUARANTINED' | 'DELETED';
        createdAt: string;
        updatedAt: string;
    };
}

export interface CreateUploadResponse {
    fileId: string;
    patientFileId: string;
    objectPath: string;
    uploadUrl: string;
    headers: Record<string, string>;
}

export interface CompleteUploadResponse {
    ok: boolean;
    patientFile: PatientFile;
}

interface BackendErrorResponse {
    response?: { data?: { message?: string } };
}

/** Surfaces the backend's structured error message instead of axios's generic
 *  "Request failed with status code N", falling back when none is present. */
function extractBackendErrorMessage(err: unknown, fallback: string): string {
    const backendMessage = (err as BackendErrorResponse)?.response?.data?.message;
    return backendMessage || fallback;
}

/**
 * Creates a signed upload URL for a patient file
 */
export async function createUpload(
    patientId: string,
    file: File,
    fileType: PatientFileType,
    description?: string,
    metadata?: Record<string, any>,
    timelineEventId?: string
): Promise<CreateUploadResponse> {
    try {
        const response = await api.post<CreateUploadResponse>(
            `/patients/${patientId}/cloud-files/upload`,
            {
                fileType,
                originalFilename: file.name,
                mimeType: file.type || 'application/octet-stream',
                sizeBytes: file.size,
                description,
                metadata,
                timelineEventId
            }
        );
        return response.data;
    } catch (err) {
        throw new Error(
            extractBackendErrorMessage(err, 'Failed to start file upload. Please try again.')
        );
    }
}

// Maximum number of retry attempts after the initial try (total = MAX_RETRIES + 1 attempts)
const GCS_UPLOAD_MAX_RETRIES = 2;
// Base delay in ms — multiplied by attempt index (1 s, 2 s)
const GCS_UPLOAD_RETRY_BASE_MS = 1000;

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

interface GcsAttemptError extends Error {
    /** HTTP status returned by GCS, or 0 for a network-level failure, -1 for abort */
    gcsHttpStatus: number;
}

function isGcsStatusRetryable(status: number): boolean {
    // Retry on network-level failure (status 0) or any 5xx server error.
    // Never retry on 4xx — those are client-side errors that won't succeed on re-attempt.
    // Never retry on -1 (user abort).
    return status === 0 || (status >= 500 && status < 600);
}

/**
 * Single PUT attempt to GCS — resolves on 2xx, rejects with a GcsAttemptError otherwise.
 */
function attemptGcsUpload(
    file: File,
    uploadUrl: string,
    headers: Record<string, string>,
    onProgress?: (progress: number) => void
): Promise<void> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && onProgress) {
                onProgress((e.loaded / e.total) * 100);
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                const errorText = xhr.responseText || xhr.statusText || 'Unknown error';
                const err = new Error(
                    `Failed to upload file to GCS (${xhr.status}): ${errorText}`
                ) as GcsAttemptError;
                err.gcsHttpStatus = xhr.status;
                reject(err);
            }
        });

        xhr.addEventListener('error', () => {
            const err = new Error(
                'Network error occurred during upload. Please check your connection.'
            ) as GcsAttemptError;
            err.gcsHttpStatus = 0;
            reject(err);
        });

        xhr.addEventListener('abort', () => {
            const err = new Error('Upload was cancelled') as GcsAttemptError;
            err.gcsHttpStatus = -1;
            reject(err);
        });

        xhr.open('PUT', uploadUrl);
        Object.entries({
            ...headers,
            'Content-Type': file.type || 'application/octet-stream'
        }).forEach(([key, value]) => xhr.setRequestHeader(key, value));
        xhr.send(file);
    });
}

/**
 * Uploads a file directly to Google Cloud Storage using a signed URL with progress
 * tracking and automatic retry on transient failures (network errors and 5xx responses).
 *
 * Retries up to GCS_UPLOAD_MAX_RETRIES times with linear back-off.
 * 4xx responses and user aborts are NOT retried.
 *
 * Signed URLs expire after 10 minutes, so a retry of a large/slow upload may
 * start past that point. When `refreshCredentials` is provided, each retry
 * fetches a fresh URL first; if that fails, the previous URL is reused
 * (no worse than not refreshing).
 */
export async function uploadToGCS(
    file: File,
    uploadUrl: string,
    headers: Record<string, string>,
    onProgress?: (progress: number) => void,
    refreshCredentials?: () => Promise<{ uploadUrl: string; headers: Record<string, string> }>
): Promise<void> {
    let lastError: Error | null = null;
    let currentUrl = uploadUrl;
    let currentHeaders = headers;

    for (let attempt = 0; attempt <= GCS_UPLOAD_MAX_RETRIES; attempt++) {
        if (attempt > 0) {
            await sleep(GCS_UPLOAD_RETRY_BASE_MS * attempt);
            if (refreshCredentials) {
                try {
                    const fresh = await refreshCredentials();
                    currentUrl = fresh.uploadUrl;
                    currentHeaders = fresh.headers;
                } catch (refreshError) {
                    console.warn(
                        'Failed to refresh upload URL, retrying with the previous one:',
                        refreshError
                    );
                }
            }
            // Reset progress indicator so the UI doesn't stay at a stale value
            onProgress?.(0);
        }

        try {
            await attemptGcsUpload(file, currentUrl, currentHeaders, onProgress);
            return;
        } catch (err) {
            lastError = err instanceof Error ? err : new Error(String(err));
            const httpStatus = (err as GcsAttemptError).gcsHttpStatus ?? -1;

            if (!isGcsStatusRetryable(httpStatus) || attempt >= GCS_UPLOAD_MAX_RETRIES) {
                throw lastError;
            }
            // retryable + more attempts remain → loop continues
        }
    }

    // Unreachable, but satisfies TypeScript
    throw lastError!;
}

/**
 * Re-signs the upload URL for an in-flight upload (file still in UPLOADING
 * status). Used by upload retries that may outlive the 10-minute expiry of
 * the original signed URL.
 */
export async function refreshUploadUrl(
    patientId: string,
    patientFileId: string
): Promise<{ uploadUrl: string; headers: Record<string, string> }> {
    const response = await api.post<{ uploadUrl: string; headers: Record<string, string> }>(
        `/patients/${patientId}/cloud-files/${patientFileId}/upload-url`
    );
    return response.data;
}

/**
 * Completes a file upload and verifies it in cloud storage
 */
export async function completeUpload(
    patientId: string,
    patientFileId: string
): Promise<CompleteUploadResponse> {
    try {
        const response = await api.post<CompleteUploadResponse>(
            `/patients/${patientId}/cloud-files/${patientFileId}/complete`
        );
        return response.data;
    } catch (err) {
        throw new Error(
            extractBackendErrorMessage(err, 'Failed to finalize file upload. Please try again.')
        );
    }
}

/**
 * Gets all files for a patient, optionally filtered by file type
 */
export async function getFiles(
    patientId: string,
    fileType?: PatientFileType,
    timelineEventId?: string
): Promise<PatientFile[]> {
    const params: Record<string, string> = {};
    if (fileType) params.fileType = fileType;
    if (timelineEventId) params.timelineEventId = timelineEventId;
    const response = await api.get<PatientFile[]>(`/patients/${patientId}/cloud-files`, { params });
    return response.data;
}

/**
 * Deletes a patient file
 */
export async function deleteFile(patientId: string, patientFileId: string): Promise<void> {
    await api.delete(`/patients/${patientId}/cloud-files/${patientFileId}`);
}

/**
 * Gets a download URL for a patient file (cached on backend)
 * @param patientId - The patient ID
 * @param patientFileId - The patient file ID
 * @param expiresInMs - Optional expiration time in milliseconds (default: 1 hour)
 * @returns The download URL
 */
export async function getDownloadUrl(
    patientId: string,
    patientFileId: string,
    expiresInMs?: number
): Promise<string> {
    const params = expiresInMs ? { expiresInMs: expiresInMs.toString() } : {};
    try {
        const response = await api.get<{ downloadUrl: string }>(
            `/patients/${patientId}/cloud-files/${patientFileId}/download-url`,
            { params }
        );
        return response.data.downloadUrl;
    } catch (err) {
        throw new Error(extractBackendErrorMessage(err, 'This document could not be loaded.'));
    }
}

/**
 * Gets a signed PUT URL for uploading a thumbnail for a given file ID.
 * The thumbnail must be a 320px WebP (content-type image/webp).
 */
export async function fetchThumbnailUploadUrl(patientId: string, fileId: string): Promise<string> {
    const response = await api.post<{ uploadUrl: string }>(
        `/patients/${patientId}/cloud-files/thumbnail-upload-url`,
        { fileId }
    );
    return response.data.uploadUrl;
}

async function uploadThumbnailBestEffort(
    patientId: string,
    fileId: string,
    file: File
): Promise<void> {
    const thumbnail = await createImageThumbnail(file);
    if (!thumbnail) return;

    const uploadUrl = await fetchThumbnailUploadUrl(patientId, fileId);

    const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/webp' },
        body: thumbnail
    });
    if (!response.ok) {
        throw new Error(`Thumbnail upload failed with status ${response.status}`);
    }
}

/**
 * Complete upload flow: create upload, upload to GCS, and complete.
 * For image files, also generates and uploads a thumbnail in the background (best-effort).
 */
export async function uploadFile(
    patientId: string,
    file: File,
    fileType: PatientFileType,
    description?: string,
    metadata?: Record<string, any>,
    onProgress?: (progress: number) => void,
    timelineEventId?: string
): Promise<PatientFile> {
    // Step 1: Create upload and get signed URL
    const createResult = await createUpload(
        patientId,
        file,
        fileType,
        description,
        metadata,
        timelineEventId
    );

    // Step 2: Upload file directly to GCS with progress tracking
    await uploadToGCS(file, createResult.uploadUrl, createResult.headers, onProgress, () =>
        refreshUploadUrl(patientId, createResult.patientFileId)
    );

    // Step 3: Complete the upload
    const completeResult = await completeUpload(patientId, createResult.patientFileId);

    // Step 4: Thumbnail upload for images — awaited but non-fatal. The original
    // upload has already completed, so a thumbnail failure must never fail it.
    if (categorizeAsset(file.name) === 'Images') {
        try {
            await uploadThumbnailBestEffort(patientId, createResult.fileId, file);
        } catch (err) {
            // Best-effort: never fail the upload, but surface the failure so it
            // isn't a silent black hole (visible in devtools / any future SDK).
            console.warn('Thumbnail generation/upload failed (non-fatal):', err);
        }
    }

    return completeResult.patientFile;
}
